import React, { useCallback, useEffect, useMemo } from "react";
import { FiCheck, FiMoon, FiSun } from "react-icons/fi";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import { getOnboardingMessages } from "../onboardingI18n";

export default function ThemeSelectionStep() {
  const { themeId, themes, setThemeId, languageId } = usePmsTheme();
  const { clearStepCommandState, markStepComplete, setStepCommandState, steps } = useOnboardingFlow();
  const copy = getOnboardingMessages(languageId);
  const stepNumber = useMemo(
    () => Math.max(steps.findIndex((step) => step.id === "theme") + 1, 1),
    [steps]
  );

  const handleSave = useCallback(async () => {
    markStepComplete("theme");
  }, [markStepComplete]);

  useEffect(() => {
    markStepComplete("theme");
  }, [markStepComplete]);

  useEffect(() => {
    setStepCommandState("theme", {
      onSave: handleSave,
      canSave: true,
      canProceed: true,
      isDirty: false,
      isSaving: false,
    });

    return () => {
      clearStepCommandState("theme");
    };
  }, [clearStepCommandState, handleSave, setStepCommandState]);

  return (
    <section className="onboarding-formCard onboarding-formCard--centered">
      <div className="onboarding-stepIntro">
        <div className="onboarding-eyebrow">{copy.themeStep.eyebrow}</div>
        <div className="onboarding-stepCount">{copy.progress.stepCounter(stepNumber, steps.length)}</div>
      </div>

      <h3 className="onboarding-sectionTitle">{copy.themeStep.title}</h3>
      <p className="onboarding-sectionText">{copy.themeStep.copy}</p>

      <div className="onboarding-themeGrid">
        {themes.map((option) => {
          const isActive = option.id === themeId;
          const ThemeIcon = option.id === "light" ? FiSun : FiMoon;
          const optionCopy = copy.themeStep[option.id] || {};

          return (
            <button
              key={option.id}
              type="button"
              className={`onboarding-themeCard ${isActive ? "onboarding-themeCard--active" : ""}`}
              onClick={() => setThemeId(option.id)}
            >
              <div className="onboarding-themeSwatches">
                {[
                  option.preview.shell,
                  option.preview.sidebar,
                  option.preview.surface,
                  option.preview.sidebarAccent,
                  option.preview.highlight,
                ].map((color, index) => (
                  <span
                    key={`${option.id}-swatch-${index}`}
                    className="onboarding-themeSwatch"
                    style={{ background: color }}
                  />
                ))}
              </div>

              <div className="onboarding-themeCardHead">
                <div className="onboarding-themeCardTitle">
                  <ThemeIcon size={16} />
                  <span>{optionCopy.title || option.label}</span>
                </div>

                {isActive ? (
                  <span className="onboarding-themeCardBadge">
                    <FiCheck size={14} />
                    {copy.themeStep.selectedTag}
                  </span>
                ) : null}
              </div>

              <div className="onboarding-themeCardMeta">{optionCopy.description || option.description}</div>

              <div className="onboarding-themeCardTags">
                {option.id === "dark" ? (
                  <>
                    <span className="onboarding-themeCardTag onboarding-themeCardTag--recommended">
                      {copy.themeStep.recommendedTag}
                    </span>
                    <span className="onboarding-themeCardTag">{copy.themeStep.originalTag}</span>
                  </>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      <p className="onboarding-note">{copy.themeStep.helper}</p>
    </section>
  );
}
